import { getCookie, retrieveSessionFromStorage } from '_helpers/queryInterceptorHelpers';
import posthog from '_services/posthog';
import type { Login, LoginStatus, Session} from '@/types/auth';

/**
 * Retrieve the login ID. First searches in session storage, then cookies.
 *
 * @returns The login ID, or `null` if none exists.
 */
export function getLoginId(): string | null {
  const session: Session | null = retrieveSessionFromStorage();
  const cookieLoginId: string = getCookie("loginid");
  return session?.id || cookieLoginId || null;
}

/**
 * Check whether to display an error message on the login screen.
 *
 * @param loginStatus - The login status object.
 * @returns `true` if an error message should be displayed; otherwise `false`.
 */
export function checkForErrorMessage(loginStatus: LoginStatus): boolean {
  const isStatusRepeated: boolean = loginStatus.previousStatusCodes.includes(loginStatus.currentStatusCode);
  const statusCodeStartsWithM: boolean = loginStatus.currentStatusCode.startsWith("M");
  const hasErrorMessage: boolean = !!loginStatus.errorMessage

  return hasErrorMessage && (!statusCodeStartsWithM || isStatusRepeated);
}

/**
 * Record a new login in PostHog.
 *
 * @param login - The newly created login object.
 * @param loginId - The existing login ID, if reloading an active session.
 */
export function captureLogin(login: Login, loginId: string | null): void {
  // Exit early if reloading an existing session or if the login lacks an authentication token
  if (loginId || !login?.token) return;

  posthog.capture('respondent_logged_in', {
    respondentId: login.loginCode.respondent,
  });
}

/**
 * Store session information when a login ID and JWT tokens are returned by the API.
 *
 * @param loginId - The login ID returned from the API.
 * @param accessToken - The access token returned from the API.
 * @param refreshToken - The refresh token returned from the API.
 * @returns void
 */
export function storeSessionInfo(loginId: string, accessToken: string | undefined, refreshToken: string | undefined): void {
  const hasCookieLoginId: boolean = !!getCookie("loginid");
  if (!loginId || !accessToken || !refreshToken || hasCookieLoginId) return;

  const session = {
    'id': loginId,
    'accessToken': accessToken,
    'refreshToken': refreshToken
  };
  const sessionJson = JSON.stringify(session);
  sessionStorage.setItem('session', sessionJson);
}

/**
 * Update the list of status codes, appending the current status code to the list of previous ones.
 *
 * @param loginStatus - The LoginStatus object for which to revise status codes.
 * @return A list of the unique status codes the respondent has received.
 */
export function updatePreviousStatusCodes(loginStatus: LoginStatus): string[] {
  return [...new Set([...loginStatus.previousStatusCodes, loginStatus.currentStatusCode])];
}